import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ImageCarousel } from '@/components/ImageCarousel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Tick = () => (
  <svg className="w-[15px] h-[15px] flex-none mt-[5px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 6L9 17l-5-5" />
  </svg>
);

const priceFrom = (l) => {
  const map = l.monthly_prices || {};
  const vals = Object.values(map).filter((v) => typeof v === 'number' && v > 0);
  return vals.length ? Math.min(...vals) : l.monthly_price;
};

export const Home = () => {
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);

  useEffect(() => {
    const d = new Date();
    const month = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    axios
      .get(`${API}/listings`, { params: { available_months: month, limit: 8 } })
      .then(({ data }) => setListings(data?.listings || []))
      .catch(() => setListings([]));
  }, []);

  const areas = [
    { key: 'Sea Point', tag: 'Promenade mornings', img: '/areas/sea-point.jpg', span: 'm1', neighbourhoods: ['Sea Point'] },
    { key: 'Gardens & City Bowl', tag: 'Under the mountain', img: 'https://images.unsplash.com/photo-1733886870851-82f45e3fad3c?auto=format&fit=crop&w=1000&q=80', span: 'm2', neighbourhoods: ['Gardens', 'City Bowl', 'Oranjezicht', 'Tamboerskloof', 'Vredehoek'] },
    { key: 'Camps Bay & Clifton', tag: 'Sunset side', img: '/areas/camps-bay-clifton.jpg', span: 'm3', neighbourhoods: ['Camps Bay', 'Clifton', 'Bantry Bay', 'Bakoven'] },
    { key: 'Green Point / De Waterkant', tag: 'Stadium, fan walk and the Waterfront', img: '/areas/green-point.jpg', span: 'm4', neighbourhoods: ['Green Point', 'De Waterkant', 'Mouille Point'] },
    { key: 'CBD', tag: 'Walk to everything', img: '/areas/cbd.jpg', span: 'm5', neighbourhoods: ['City Bowl', 'Zonnebloem', 'Bo-Kaap'] },
  ];

  const openArea = (a) => {
    const params = new URLSearchParams();
    params.set('neighbourhoods', a.neighbourhoods.join(','));
    params.set('area', a.key);
    navigate(`/listings?${params.toString()}`);
  };

  return (
    <div className="bg-paper text-ink" data-testid="home-page">
      {/* ============ HERO ============ */}
      <section className="pt-[66px]">
        <div className="wrap grid lg:grid-cols-[1.08fr_0.92fr] gap-10 items-end">
          <div>
            <div className="mono">Furnished monthly accommodation</div>
            <h1
              className="mt-4 font-display leading-[1.06] tracking-[-0.035em]"
              style={{ fontSize: 'clamp(36px, 4.7vw, 62px)' }}
              data-testid="home-hero-title"
            >
              Stay for a month<br />or more in<br />
              <span className="font-display-italic" style={{ fontSize: '1.04em', letterSpacing: '-0.025em' }}>
                Cape&nbsp;Town
              </span>
            </h1>
            <p className="mt-6 text-[18px] text-ink-2 max-w-[46ch]" data-testid="home-hero-subtitle">
              Cape Town Monthly lists furnished places for Capetonians and newcomers alike, for the stretch that is too long for a nightly rate and too short for a twelve-month lease. Stays start at 28 nights, browsing is free, and you deal with the owner directly.
            </p>
          </div>

          <div>
            <div className="flex gap-2 flex-wrap mb-5">
              <span className="pill on">Locals welcome</span>
              <span className="pill">Visitors welcome</span>
              <span className="pill">Free for guests</span>
            </div>
            <div className="grid grid-cols-2 gap-3.5 items-end">
              <div className="ph rounded-[10px] row-span-2 aspect-[3/4]">
                <img alt="A furnished living room with a large window" className="w-full h-full object-cover" src="https://images.unsplash.com/photo-1665249934445-1de680641f50?auto=format&fit=crop&w=900&q=80" />
              </div>
              <div className="ph rounded-[10px] aspect-[4/3]">
                <img alt="Cape Town's Atlantic seaboard from above" className="w-full h-full object-cover" src="https://images.unsplash.com/photo-1580060839134-75a5edca2e99?auto=format&fit=crop&w=900&q=80" />
              </div>
              <div className="ph rounded-[10px] aspect-square">
                <img alt="A made bed beside a bright window" className="w-full h-full object-cover" src="https://images.unsplash.com/photo-1585128792103-0b591f96512e?auto=format&fit=crop&w=700&q=80" />
              </div>
              <div className="col-span-2 text-[12.5px] text-muted flex justify-between gap-3 pt-0.5">
                <span>Sea Point · Gardens · Camps Bay</span>
                <span>Verified places</span>
              </div>
            </div>
          </div>
        </div>

        {/* Search band */}
        <div className="mt-14 border-y" style={{ background: 'var(--paper-2)', borderColor: 'var(--line)' }}>
          <div className="wrap">
            <div className="grid md:grid-cols-[1.1fr_1fr_1fr_auto] items-stretch">
              <div className="p-6 md:pl-0 md:pr-6 md:border-r" style={{ borderColor: 'var(--line)' }}>
                <div className="text-[11px] uppercase tracking-[0.16em] font-semibold text-muted">Area</div>
                <div className="font-display text-[18px] font-medium mt-0.5">Anywhere in Cape Town</div>
              </div>
              <div className="p-6 md:border-r" style={{ borderColor: 'var(--line)' }}>
                <div className="text-[11px] uppercase tracking-[0.16em] font-semibold text-muted">Moving in</div>
                <div className="font-display text-[18px] font-medium mt-0.5">Any month</div>
              </div>
              <div className="p-6">
                <div className="text-[11px] uppercase tracking-[0.16em] font-semibold text-muted">Staying</div>
                <div className="font-display text-[18px] font-medium mt-0.5">28 nights or more</div>
              </div>
              <div className="p-4 md:p-6 flex items-center">
                <button
                  onClick={() => navigate('/listings')}
                  className="btn btn-sea btn-lg w-full md:w-auto"
                  data-testid="home-search-btn"
                >
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
                    <circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" />
                  </svg>
                  Show me what&apos;s open
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ 01 THE GAP ============ */}
      <section className="py-24" data-testid="home-gap-section">
        <div className="wrap">
          <div className="grid grid-cols-[74px_1fr] gap-6 pt-6 border-t" style={{ borderColor: 'var(--line-2)' }}>
            <div className="font-display text-[14px] font-semibold text-muted pt-1.5 tracking-[0.04em]">01</div>
            <div>
              <h2 className="font-display" style={{ fontSize: 'clamp(28px, 3.6vw, 44px)' }}>
                The month-long stay is the one nobody caters for.
              </h2>
              <p className="text-ink-2 text-[17.5px] mt-4 max-w-[58ch]">
                Cape Town is well supplied for a long weekend and for a year. Almost nothing sits in between.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-4 mt-12">
            {[
              ['01', 'Too short for nightly', 'A rate that\'s fair for four nights isn\'t fair for four months.'],
              ['02', 'Too long for a lease', 'Standard leases want twelve months, unfurnished, with credit checks.'],
              ['03', 'Never advertised', 'Monthly places get filled by word of mouth, before anyone can search for them.'],
            ].map(([n, h, p]) => (
              <div key={n} className="card-b">
                <div className="font-display text-[38px] font-semibold leading-none tracking-[-0.03em]">{n}</div>
                <h4 className="mt-4 text-[21px] tracking-[-0.02em]">{h}</h4>
                <p className="text-ink-2 text-[15.5px] leading-[1.6] mt-2">{p}</p>
              </div>
            ))}
          </div>

          <div className="mt-11 grid md:grid-cols-[1.25fr_0.75fr] gap-10 items-center">
            <div>
              <h3 className="font-display leading-[1.16]" style={{ fontSize: 'clamp(24px, 3.1vw, 38px)' }}>
                So we put them somewhere you can actually find them,<br />
                <span className="font-display-italic" style={{ fontSize: '1.22em', letterSpacing: 0 }}>
                  then we get out of the way.
                </span>
              </h3>
              <p className="text-ink-2 mt-4 max-w-[52ch]">
                Cape Town Monthly lists furnished places from 28 nights, each one checked before it is published and listed by the owner. You deal with them directly, agree your own terms, and nothing about the arrangement runs through us.
              </p>
              <div className="mt-7 flex gap-3 flex-wrap">
                <button onClick={() => navigate('/listings')} className="btn btn-sea btn-lg" data-testid="home-see-open-btn">
                  See what&apos;s open
                </button>
                <button onClick={() => navigate('/pricing')} className="btn btn-line btn-lg">
                  What it costs
                </button>
              </div>
            </div>
            <div className="ph rounded-[10px] aspect-[5/4]">
              <img alt="Cape Town's city bowl beside the bay" className="w-full h-full object-cover" src="https://images.unsplash.com/photo-1529528070131-eda9f3e90919?auto=format&fit=crop&w=1200&q=80" />
            </div>
          </div>
        </div>
      </section>

      {/* ============ 02 WHO IT'S FOR ============ */}
      <section className="pb-24">
        <div className="wrap">
          <div className="grid grid-cols-[74px_1fr] gap-6 pt-6 border-t" style={{ borderColor: 'var(--line-2)' }}>
            <div className="font-display text-[14px] font-semibold text-muted pt-1.5 tracking-[0.04em]">02</div>
            <div>
              <h2 className="font-display" style={{ fontSize: 'clamp(28px, 3.6vw, 44px)' }}>Who ends up here.</h2>
              <p className="text-ink-2 text-[17.5px] mt-4 max-w-[58ch]">
                Not one type of person. Roughly half our guests already live in Cape Town.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-[1.15fr_0.85fr] gap-10 mt-12 items-stretch">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                ['Between leases', 'Your old place ends in March, the new one isn\'t ready until May. Two months is the length nobody wants to rent you.'],
                ['Here on a contract', 'Three or six months of work in the city, and no appetite for a year\'s lease before you\'ve seen a suburb.'],
                ['Renovating', 'The builders said six weeks. You need somewhere furnished and nearby for however long that turns out to be.'],
                ['Landing softly', 'New to the country or the city, and you\'d rather learn the neighbourhoods before committing to one.'],
              ].map(([h, p]) => (
                <div key={h} className="card-b">
                  <h4 className="text-[21px] tracking-[-0.02em]">{h}</h4>
                  <p className="text-ink-2 text-[15.5px] leading-[1.6] mt-2">{p}</p>
                </div>
              ))}
            </div>
            <div className="ph rounded-[10px] min-h-[400px]">
              <img alt="Someone working at a table in a bright furnished flat" className="w-full h-full object-cover" src="https://images.unsplash.com/photo-1503945438517-f65904a52ce6?auto=format&fit=crop&w=1100&q=80" />
            </div>
          </div>
        </div>
      </section>

      {/* ============ 03 AVAILABLE ============ */}
      {listings.length > 0 && (
      <section className="pb-14" id="available" data-testid="home-open-now-section">
        <div className="wrap">
          <div className="grid grid-cols-[74px_1fr] gap-6 pt-6 border-t" style={{ borderColor: 'var(--line-2)' }}>
            <div className="font-display text-[14px] font-semibold text-muted pt-1.5 tracking-[0.04em]">03</div>
            <div className="flex items-end justify-between gap-5 flex-wrap">
              <div>
                <h2 className="font-display" style={{ fontSize: 'clamp(28px, 3.6vw, 44px)' }}>Open now.</h2>
                <p className="text-ink-2 text-[17.5px] mt-4 max-w-[58ch]">
                  Prices are set by the owner, not by us, and they&apos;re the ones you negotiate with.
                </p>
              </div>
              <button onClick={() => navigate('/listings')} className="btn btn-line" data-testid="home-view-all-btn">
                View now →
              </button>
            </div>
          </div>
        </div>

        {/* Horizontal rail */}
        <div
          className="mt-12 grid gap-6 overflow-x-auto pb-4"
          style={{
            gridAutoFlow: 'column',
            gridAutoColumns: '330px',
            scrollSnapType: 'x mandatory',
            padding: '0 28px 24px',
          }}
        >
          {listings.map((l) => (
              <article
                key={l.id}
                className="scroll-snap-align-start cursor-pointer group"
                style={{ scrollSnapAlign: 'start' }}
                onClick={() => navigate(`/listings/${l.id}`)}
                data-testid={`home-listing-${l.id}`}
              >
                <div className="relative aspect-[4/3] rounded-[10px] overflow-hidden mb-4">
                  <ImageCarousel photos={l.photos || []} alt={l.title} />
                  {l.verification_status === 'approved' && (
                    <span className="absolute left-3 top-3 z-[3] bg-white rounded-md px-2.5 py-1.5 text-[11px] tracking-[0.1em] uppercase font-bold text-sea">
                      Checked
                    </span>
                  )}
                </div>
                <h4 className="font-display text-[19px] tracking-[-0.02em]">{l.title}</h4>
                <p className="text-muted text-[14px] mt-1.5">{l.neighbourhood}</p>
                <div className="flex items-center justify-between gap-3 mt-3.5 pt-3 border-t" style={{ borderColor: 'var(--line)' }}>
                  <div className="font-display text-[19px] font-semibold">
                    R{Number(priceFrom(l) || 0).toLocaleString('en-ZA')}
                    <span className="ml-1 font-sans text-[13px] font-normal text-muted">/ month</span>
                  </div>
                </div>
              </article>
          ))}
        </div>
      </section>
      )}

      {/* ============ 04 AREAS ============ */}
      <section className="pt-10 pb-24" id="areas">
        <div className="wrap">
          <div className="grid grid-cols-[74px_1fr] gap-6 pt-6 border-t" style={{ borderColor: 'var(--line-2)' }}>
            <div className="font-display text-[14px] font-semibold text-muted pt-1.5 tracking-[0.04em]">04</div>
            <div>
              <h2 className="font-display" style={{ fontSize: 'clamp(28px, 3.6vw, 44px)' }}>
                Areas we have monthly stays in.
              </h2>
            </div>
          </div>

          <div className="grid grid-cols-12 gap-4 mt-12">
            {areas.map((a, i) => {
              const spans = {
                m1: 'md:col-span-5 h-[300px] md:h-[420px]',
                m2: 'md:col-span-4 h-[300px] md:h-[420px]',
                m3: 'md:col-span-3 h-[300px] md:h-[420px]',
                m4: 'md:col-span-6 h-[260px] md:h-[300px]',
                m5: 'md:col-span-6 h-[260px] md:h-[300px]',
              }[a.span];
              return (
                <button
                  key={a.key}
                  onClick={() => openArea(a)}
                  className={`area-tile relative rounded-[10px] overflow-hidden text-left col-span-12 ${spans}`}
                  data-testid={`home-area-${i}`}
                >
                  <img
                    alt={a.key}
                    src={a.img}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                  />
                  <span
                    className="absolute inset-0"
                    style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0) 40%, rgba(0,0,0,.8))' }}
                  />
                  <div className="relative z-[2] h-full flex flex-col justify-end p-5 text-white">
                    <h4 className="text-[21px]">{a.key}</h4>
                    <span className="text-[13.5px]" style={{ color: 'rgba(255,255,255,0.82)' }}>{a.tag}</span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============ 05 STAYING ============ */}
      <section className="pb-0" id="staying">
        <div className="wrap">
          <div className="grid grid-cols-[74px_1fr] gap-6 pt-6 border-t" style={{ borderColor: 'var(--line-2)' }}>
            <div className="font-display text-[14px] font-semibold text-muted pt-1.5 tracking-[0.04em]">05</div>
            <div>
              <h2 className="font-display" style={{ fontSize: 'clamp(28px, 3.6vw, 44px)' }}>If you&apos;re the one looking.</h2>
              <p className="text-ink-2 text-[17.5px] mt-4 max-w-[58ch]">
                Browsing, enquiring and arranging a stay costs you nothing at any point.
              </p>
            </div>
          </div>

          <div
            className="mt-12 grid md:grid-cols-2 rounded-xl overflow-hidden border"
            style={{ borderColor: 'var(--line)' }}
          >
            <div className="ph min-h-[420px]">
              <img alt="A furnished living room" className="w-full h-full object-cover" src="https://images.unsplash.com/photo-1661796428215-04fc2830aae6?auto=format&fit=crop&w=1100&q=80" />
            </div>
            <div className="p-10 md:p-12 bg-white">
              <h3 className="font-display leading-[1.16]" style={{ fontSize: 'clamp(25px, 3vw, 34px)', maxWidth: '18ch' }}>
                Furnished, ready, and yours for as long as you need it
              </h3>
              <p className="text-ink-2 mt-4 max-w-[44ch]">
                Plates in the cupboard, wifi already on, a bed that&apos;s made. Every place takes stays of 28 nights or more, so nobody&apos;s doing you a favour by letting you stay a month.
              </p>
              <ol className="mt-6 list-none p-0" style={{ counterReset: 's' }}>
                {[
                  ['Search by area, month and budget', 'Only places that take month-long stays'],
                  ['Open the listing', 'Photos, full details, the owner\'s rate and what\'s included'],
                  ['Message the owner yourself', 'WhatsApp or email. Ask about wifi, noise, parking'],
                  ['Settle the terms between you', 'Rate, dates, deposit and lease are yours to agree'],
                ].map(([t, s], i) => (
                  <li key={i} className="flex gap-4 py-3.5 border-t" style={{ borderColor: 'var(--line)' }}>
                    <span className="font-display font-semibold text-[13px] flex-none pt-[3px]">
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    <div>
                      <strong className="block text-[16px] font-semibold">{t}</strong>
                      <span className="text-[14.5px] text-muted">{s}</span>
                    </div>
                  </li>
                ))}
              </ol>
              <button onClick={() => navigate('/listings')} className="btn btn-sea btn-lg mt-7">
                Browse what&apos;s open
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ============ 06 HOSTING ============ */}
      <section className="py-24" id="hosting">
        <div className="wrap">
          <div className="grid grid-cols-[74px_1fr] gap-6 pt-6 border-t" style={{ borderColor: 'var(--line-2)' }}>
            <div className="font-display text-[14px] font-semibold text-muted pt-1.5 tracking-[0.04em]">06</div>
            <div>
              <h2 className="font-display" style={{ fontSize: 'clamp(28px, 3.6vw, 44px)' }}>If you&apos;re the one letting.</h2>
              <p className="text-ink-2 text-[17.5px] mt-4 max-w-[58ch]">
                You set the rate, you choose the tenant, you keep everything you charge.
              </p>
            </div>
          </div>

          <div
            className="mt-12 grid md:grid-cols-2 rounded-xl overflow-hidden border"
            style={{ borderColor: 'var(--line)' }}
          >
            <div className="p-10 md:p-12 bg-white order-2 md:order-1">
              <h3 className="font-display leading-[1.16]" style={{ fontSize: 'clamp(25px, 3vw, 34px)', maxWidth: '18ch' }}>
                One tenant for the season instead of forty for the weekend
              </h3>
              <p className="text-ink-2 mt-4 max-w-[44ch]">
                Longer stays mean fewer cleans, fewer key handovers, and somebody who settles in rather than passes through. We advertise the place and confirm it&apos;s yours to let. Everything after that is between you and the guest.
              </p>
              <ol className="mt-6 list-none p-0">
                {[
                  ['Create a host account', 'A couple of minutes'],
                  ['Add your photos, details and rate', 'You decide what to charge and who to accept'],
                  ['Send us a walkthrough video', 'Filmed on your phone. It\'s how we confirm the place is yours to let'],
                  ['Go live and take enquiries yourself', 'Usually within a day; guests then contact you directly'],
                ].map(([t, s], i) => (
                  <li key={i} className="flex gap-4 py-3.5 border-t" style={{ borderColor: 'var(--line)' }}>
                    <span className="font-display font-semibold text-[13px] flex-none pt-[3px]">
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    <div>
                      <strong className="block text-[16px] font-semibold">{t}</strong>
                      <span className="text-[14.5px] text-muted">{s}</span>
                    </div>
                  </li>
                ))}
              </ol>
              <button onClick={() => navigate('/list-property')} className="btn btn-ink btn-lg mt-7" data-testid="home-list-your-place-btn">
                List your place
              </button>
            </div>
            <div className="ph min-h-[420px] order-1 md:order-2">
              <img alt="A clean, light kitchen in a furnished flat" className="w-full h-full object-cover" src="https://images.unsplash.com/photo-1593853761096-d0423b545cf9?auto=format&fit=crop&w=1100&q=80" />
            </div>
          </div>
        </div>
      </section>

      {/* ============ 07 COST ============ */}
      <section className="pb-24" id="cost">
        <div className="wrap">
          <div className="grid grid-cols-[74px_1fr] gap-6 pt-6 border-t" style={{ borderColor: 'var(--line-2)' }}>
            <div className="font-display text-[14px] font-semibold text-muted pt-1.5 tracking-[0.04em]">07</div>
            <div>
              <h2 className="font-display" style={{ fontSize: 'clamp(28px, 3.6vw, 44px)' }}>What it costs.</h2>
              <p className="text-ink-2 text-[17.5px] mt-4 max-w-[58ch]">
                One fee, on one side. No commission, and no percentage of anybody&apos;s rent.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4 mt-12">
            <div className="rounded-[10px] p-9 border" style={{ borderColor: 'var(--line-2)' }}>
              <div className="text-[12px] uppercase tracking-[0.16em] font-semibold text-muted">Guests pay</div>
              <div className="font-display font-semibold mt-3 leading-none tracking-[-0.04em]" style={{ fontSize: 'clamp(42px, 5.8vw, 68px)' }}>
                Nothing
              </div>
              <div className="text-muted text-[15px] mt-1.5">At any stage</div>
              <ul className="mt-6 grid gap-2.5 list-none p-0 text-ink-2 text-[15px]">
                {['No booking or service fee', 'Unlimited browsing and enquiries', 'The owner\'s own contact details'].map((t) => (
                  <li key={t} className="flex gap-3 items-start"><Tick />{t}</li>
                ))}
              </ul>
            </div>
            <div className="rounded-[10px] p-9 bg-ink text-white">
              <div className="text-[12px] uppercase tracking-[0.16em] font-semibold" style={{ color: 'rgba(255,255,255,0.66)' }}>Hosts pay</div>
              <div className="font-display font-semibold mt-3 leading-none tracking-[-0.04em]" style={{ fontSize: 'clamp(42px, 5.8vw, 68px)' }}>
                R950
              </div>
              <div className="text-[15px] mt-1.5" style={{ color: 'rgba(255,255,255,0.7)' }}>Every three months, flat</div>
              <ul className="mt-6 grid gap-2.5 list-none p-0 text-[15px]" style={{ color: 'rgba(255,255,255,0.86)' }}>
                {['No commission on your rate', 'No cut per booking', 'Unlimited enquiries, straight to you'].map((t) => (
                  <li key={t} className="flex gap-3 items-start"><Tick />{t}</li>
                ))}
              </ul>
            </div>
          </div>

          <p className="text-muted text-[15.5px] mt-6 max-w-[74ch]">
            <b className="text-ink font-semibold">Everything else is yours to negotiate.</b> Rates, deposits, dates, house rules and the lease are agreed directly between host and guest. Cape Town Monthly does not process payments, hold deposits, or take a percentage of anything.
          </p>
        </div>
      </section>

      {/* ============ 08 APPROVAL ============ */}
      <section className="pb-24">
        <div className="wrap">
          <div className="grid grid-cols-[74px_1fr] gap-6 pt-6 border-t" style={{ borderColor: 'var(--line-2)' }}>
            <div className="font-display text-[14px] font-semibold text-muted pt-1.5 tracking-[0.04em]">08</div>
            <div>
              <h2 className="font-display" style={{ fontSize: 'clamp(28px, 3.6vw, 44px)' }}>
                What we check before a place goes up.
              </h2>
              <p className="text-ink-2 text-[17.5px] mt-4 max-w-[58ch]">
                Renting straight from an owner shouldn&apos;t mean renting blind. Here&apos;s the whole process, and where it deliberately stops.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-[1.08fr_0.92fr] gap-10 mt-12 items-start">
            <div>
              <div className="grid gap-4">
                {[
                  ['01', 'The owner films a walkthrough', 'They send us the video before anything is published. It\'s how we confirm the place exists, matches the photos, and is theirs to let.'],
                  ['02', 'A person reviews it, inside a day', 'Nothing publishes automatically. No scraped adverts, no duplicates, no places that vanish the week before you arrive.'],
                  ['03', 'Then we stop', 'Once a listing is approved Cape Town Monthly is finished. The conversation, the terms and the money are between the two of you.'],
                ].map(([n, h, p]) => (
                  <div key={n} className="card-b">
                    <div className="font-display text-[38px] font-semibold leading-none tracking-[-0.03em]">{n}</div>
                    <h4 className="mt-4 text-[21px] tracking-[-0.02em]">{h}</h4>
                    <p className="text-ink-2 text-[15.5px] leading-[1.6] mt-2">{p}</p>
                  </div>
                ))}
              </div>
              <p className="mt-6 text-[14.5px] text-muted border-l-2 pl-4 max-w-[60ch]" style={{ borderColor: 'var(--ink)' }}>
                The walkthrough video is for our own checks only. It isn&apos;t published on the listing and isn&apos;t something guests watch while browsing.
              </p>
            </div>
            <div className="ph rounded-[10px] aspect-[4/5]">
              <img alt="A woman working at a table in a furnished apartment" className="w-full h-full object-cover" src="https://images.unsplash.com/photo-1664575198308-3959904fa430?auto=format&fit=crop&w=1000&q=80" />
            </div>
          </div>
        </div>
      </section>

      {/* ============ CLOSING ============ */}
      <div className="relative min-h-[520px] flex items-end overflow-hidden">
        <img alt="Cape Town beach at sunset" className="absolute inset-0 w-full h-full object-cover" src="https://images.unsplash.com/photo-1563656157432-67560011e209?auto=format&fit=crop&w=2000&q=80" />
        <div
          className="absolute inset-0"
          style={{ background: 'linear-gradient(180deg, rgba(0,0,0,.2) 26%, rgba(8,47,42,.92))' }}
        />
        <div className="relative z-[2] wrap py-16 text-white max-w-[640px]">
          <h2 className="font-display text-white tracking-[-0.03em]" style={{ fontSize: 'clamp(28px, 3.9vw, 48px)' }}>
            Furnished{' '}
            <span className="font-display-italic" style={{ color: '#fff', fontSize: '1.06em' }}>monthly</span>{' '}
            accommodation, for whoever needs a month.
          </h2>
          <p className="mt-4 text-[18px]" style={{ color: 'rgba(255,255,255,0.88)' }}>
            Free to browse, free to enquire, and the owner is the person you&apos;ll be talking to.
          </p>
          <div className="mt-7 flex gap-3 flex-wrap">
            <button onClick={() => navigate('/listings')} className="btn btn-paper btn-lg" data-testid="home-closing-view-btn">
              View now
            </button>
            <button onClick={() => navigate('/list-property')} className="btn btn-sea btn-lg">
              List your place
            </button>
          </div>
        </div>
      </div>

      {/* ============ FOOTER ============ */}
      <footer className="pt-16 pb-10 border-t" style={{ borderColor: 'var(--line-2)' }}>
        <div className="wrap">
          <div className="grid grid-cols-2 md:grid-cols-[2fr_1fr_1fr_1fr] gap-8">
            <div>
              <span className="flex items-baseline gap-2 mb-3">
                <span className="font-display font-bold text-[18px] tracking-[-0.03em]">Cape&nbsp;Town</span>
                <span className="font-display-italic text-[20px] leading-none">Monthly</span>
              </span>
              <p className="text-muted max-w-[40ch] mt-3">
                Cape Town Monthly lists furnished monthly accommodation across Cape Town, South Africa, from Sea Point and Gardens to Camps Bay, Green Point and the CBD. Stays of 28 nights and up, listed and let directly by the people who own the places.
              </p>
              <p className="mt-4 text-[14.5px]" data-testid="footer-help-email">
                Need help? Email{' '}
                <a href="mailto:enquiries@capetownmonthly.com" className="font-semibold underline hover:text-sea transition-colors">
                  enquiries@capetownmonthly.com
                </a>
              </p>
            </div>
            <div>
              <h5 className="text-[11.5px] tracking-[0.16em] uppercase text-muted font-semibold mb-3.5">Staying</h5>
              <a href="/listings" className="block py-1.5 font-medium hover:text-sea transition-colors">Available now</a>
              <a href="/how-it-works" className="block py-1.5 font-medium hover:text-sea transition-colors">How it works</a>
              <a href="/pricing" className="block py-1.5 font-medium hover:text-sea transition-colors">What it costs</a>
            </div>
            <div>
              <h5 className="text-[11.5px] tracking-[0.16em] uppercase text-muted font-semibold mb-3.5">Letting</h5>
              <a href="/list-property" className="block py-1.5 font-medium hover:text-sea transition-colors">List your place</a>
              <a href="/pricing" className="block py-1.5 font-medium hover:text-sea transition-colors">Listing fee</a>
              <a href="/trust-and-safety" className="block py-1.5 font-medium hover:text-sea transition-colors">What we check</a>
            </div>
            <div>
              <h5 className="text-[11.5px] tracking-[0.16em] uppercase text-muted font-semibold mb-3.5">Company</h5>
              <a href="/contact" className="block py-1.5 font-medium hover:text-sea transition-colors">Contact</a>
              <a href="#" className="block py-1.5 font-medium hover:text-sea transition-colors">Terms</a>
              <a href="#" className="block py-1.5 font-medium hover:text-sea transition-colors">Privacy</a>
            </div>
          </div>
          <div className="mt-10 pt-6 border-t flex justify-between gap-4 flex-wrap text-[13.5px] text-muted" style={{ borderColor: 'var(--line)' }}>
            <span>© 2026 Cape Town Monthly</span>
            <span>Cape Town, South Africa · Prices in ZAR</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
